/**
 * Typed client for /integrations-admin/* — backs the Integrations screen.
 *
 * Scope of this port:
 *   - Overview (KPIs across ecommerce / discord / telegram + coupon totals)
 *   - eCommerce list + detail (Shopify, WooCommerce) with status mutation
 *
 * Deferred to follow-up sessions (intentionally not typed here):
 *   - Discord guild list + per-guild detail
 *   - Telegram users + quests
 *   - Admin-wide coupons grid
 *
 * The deferred sub-tabs are surfaced as deep-link cards inside the
 * Overview tab so staff can still reach them on app.nurev.io while the
 * full port lands.
 */
import { client, unwrap } from './client';

export type EcomProvider = 'shopify' | 'woocommerce';
export type IntegrationStatus = 'active' | 'paused' | 'error' | 'disconnected';

export interface EcomKpi {
  total: number;
  active: number;
  paused?: number;
  error?: number;
}

export interface IntegrationsOverviewResponse {
  ecommerce: {
    shopify?: EcomKpi;
    woocommerce?: EcomKpi;
    totalCoupons?: number;
    redeemedCoupons?: number;
  };
  discord: {
    available: boolean;
    guilds?: number;
    users?: number;
    error?: string | null;
  };
  telegram: {
    available: boolean;
    users?: number;
    quests?: number;
    error?: string | null;
  };
}

export interface EcomPublisherRef {
  _id?: string;
  email?: string;
  publisherKey?: string;
  name?: string;
}

export interface EcomIntegrationRow {
  _id: string;
  type: EcomProvider | string;
  name?: string;
  remoteId?: string;
  status: IntegrationStatus | string;
  connectedAt?: string;
  publisherId?: EcomPublisherRef;
  stats?: {
    totalRedemptions?: number;
    totalIssued?: number;
  };
  config?: Record<string, unknown>;
}

export interface EcomIntegrationListResponse {
  docs: EcomIntegrationRow[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface EcomCouponRow {
  _id: string;
  code: string;
  discountType?: 'percent' | 'fixed';
  discountValue?: number;
  status?: string;
  createdAt: string;
}

export interface EcomIntegrationDetailResponse {
  integration: EcomIntegrationRow & { config?: Record<string, unknown> };
  recentCoupons?: EcomCouponRow[];
}

export interface EcomListParams {
  page?: number;
  limit?: number;
  q?: string;
  type?: EcomProvider | '';
  status?: IntegrationStatus | '';
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchIntegrationsOverview(): Promise<IntegrationsOverviewResponse> {
  return unwrap<IntegrationsOverviewResponse>(client.get(
    '/integrations-admin/overview'
  ));
}

export async function fetchEcommerceIntegrations(
  params: EcomListParams
): Promise<EcomIntegrationListResponse> {
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  return unwrap<EcomIntegrationListResponse>(client.get(
    '/integrations-admin/ecommerce',
    { params: cleanParams }
  ));
}

export async function fetchEcommerceIntegration(
  id: string
): Promise<EcomIntegrationDetailResponse> {
  return unwrap<EcomIntegrationDetailResponse>(client.get(
    `/integrations-admin/ecommerce/${id}`
  ));
}

export async function updateEcommerceIntegration(input: {
  id: string;
  status?: IntegrationStatus;
}): Promise<EcomIntegrationRow> {
  const { id, ...body } = input;
  return unwrap<EcomIntegrationRow>(client.patch(
    `/integrations-admin/ecommerce/${id}`,
    body
  ));
}
